import React, { useEffect, useState } from "react";
import Image from "next/image";
import styles from "./CourseFacultyLights.module.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

interface FacultyImage {
  url?: string | null;
  alternativeText?: string | null;
  width?: number | null;
  height?: number | null;
}

export interface FacultySpotlight {
  id: number;
  heading?: string | null;
  sub_heading?: string | null;
  image?: FacultyImage | null;
}

export interface FacultySpotlightSection {
  title?: string | null;
  sub_heading?: string | null;
  hide_section?: boolean;
  items?: FacultySpotlight[];
}

interface CourseFacultyLightsProps {
  data?: FacultySpotlightSection | null;
}

const CourseFacultyLights: React.FC<CourseFacultyLightsProps> = ({ data }) => {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const facultyMembers = data?.items?.filter(Boolean) || [];
  const sliderThreshold =
    viewportWidth <= 767 ? 1 : viewportWidth <= 1023 ? 2 : 3;
  const hasSlider = facultyMembers.length > sliderThreshold;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Hide section if hide_section is true, or if there are no faculty members
  if (data?.hide_section === true || facultyMembers.length === 0) {
    return null;
  }

  const renderCard = (member: FacultySpotlight) => {
    const imageSrc = member?.image?.url || "/assets/default.png";
    const imageAlt =
      member?.image?.alternativeText || member?.heading || "Faculty spotlight";
    const imageWidth = member?.image?.width || 480;
    const imageHeight = member?.image?.height || 540;

    return (
      <div className={styles.card} key={member?.id}>
        <div className={styles.imageWrapper}>
          <Image
            src={imageSrc}
            alt={imageAlt}
            title={imageAlt}
            width={imageWidth}
            height={imageHeight}
            className={styles.facultyImage}
            sizes="(max-width: 768px) 100vw, 380px"
          />
        </div>
        <div>
          {member?.heading && (
            <h3 className={styles.name}>{member.heading}</h3>
          )}

          {member?.sub_heading && (
            <p className={styles.designation}>{member.sub_heading}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className={styles.facultyLights}>
      <div className="container">
        <div className={styles.header}>
          <h2 className={styles.heading}>
            {data?.title || "Faculty Spotlights"}
          </h2>
          {data?.sub_heading && (
            <p className={styles.subHeading}>{data.sub_heading}</p>
          )}
        </div>

        {hasSlider ? (
          <div className={styles.swiperContainer}>
            <Swiper
              modules={[Pagination]}
              pagination={{ clickable: true }}
              slidesPerView={3}
              spaceBetween={24}
              className={styles.swiperInstance}
              breakpoints={{
                0: {
                  slidesPerView: 1,
                  spaceBetween: 16,
                },
                640: {
                  slidesPerView: 2,
                  spaceBetween: 20,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 24,
                },
              }}
            >
              {facultyMembers?.map((member) => (
                <SwiperSlide
                  key={member?.id}
                  className={styles.swiperSlide}
                >
                  {renderCard(member)}
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        ) : (
          <div className={styles.cardGrid}>
            {facultyMembers?.map((member) => renderCard(member))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CourseFacultyLights;

